import { Box, chakra, Span } from '@chakra-ui/react';
import { NavArrowDown } from 'iconoir-react';
import { useId, useState, type ReactNode } from 'react';

/**
 * Rail section title: a sentence-case heading (Figtree 13.5px semibold, ink),
 * not the uppercase tracked label the detail views use (`ContactSectionLabel`).
 */
const RailSectionTitle: React.FC<{ children: ReactNode }> = ({ children }) => (
  <Span
    fontFamily="heading"
    fontSize="13.5px"
    fontWeight="semibold"
    letterSpacing="-0.005em"
    color="ink"
  >
    {children}
  </Span>
);

interface ContactSectionProps {
  /** Section title, e.g. "Profils", "Collaborations", "Contact". */
  label: string;
  children: ReactNode;
  /** Optional right-aligned accessory in the header (badge / count). */
  accessory?: ReactNode;
  /** When set, the section header is a toggle button (aria-expanded/controls). */
  collapsible?: boolean;
  /** Initial open state for a collapsible section. Ignored when static. */
  defaultOpen?: boolean;
}

/**
 * Labelled rail section primitive. Renders a sentence-case section title with an
 * optional right-side accessory, then its content.
 *
 * Static by default (Profils / Collaborations). With `collapsible`, the header
 * becomes a disclosure button carrying `aria-expanded` + `aria-controls`, the
 * chevron rotates, and the body is toggled (Contact / Notes).
 */
const ContactSection: React.FC<ContactSectionProps> = ({
  label,
  children,
  accessory,
  collapsible = false,
  defaultOpen = true,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();

  if (!collapsible) {
    return (
      <Box>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          minH="18px"
          mb="11px"
        >
          <RailSectionTitle>{label}</RailSectionTitle>
          {accessory}
        </Box>
        {children}
      </Box>
    );
  }

  return (
    <Box>
      <chakra.button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={bodyId}
        display="flex"
        alignItems="center"
        gap="2"
        width="full"
        mb="11px"
        cursor="pointer"
        _focusVisible={{
          outline: '2px solid',
          outlineColor: 'primary.900',
          outlineOffset: '2px',
          rounded: 'sm',
        }}
      >
        <RailSectionTitle>{label}</RailSectionTitle>
        {accessory}
        <Box
          asChild
          ml="auto"
          color="primary.400"
          transform={open ? 'rotate(180deg)' : 'rotate(0deg)'}
          transition="transform 0.15s"
        >
          <NavArrowDown width={16} height={16} />
        </Box>
      </chakra.button>
      <Box id={bodyId} hidden={!open}>
        {children}
      </Box>
    </Box>
  );
};

export default ContactSection;
